/**
 * SMS's side of the `POST /api/v2/ws/ticket/` connection descriptor
 * (ADR-078 Amendment 1, 6-F): which socket hosts this build trusts and where
 * a refused endpoint is reported. Parsing, endpoint order and the ticket URL
 * are shared with every web client through `@tiaki/http-client`.
 */

import * as Sentry from '@sentry/react';
import {
  WS_SOCKET_PATH,
  buildSocketConnectionPlan as buildPlanFromDescriptor,
  socketEndpointHost,
  type SocketConnectionPlan,
  type SocketEndpointRejection,
} from '@tiaki/http-client';
import { environment } from '../environments/environment';

export type SocketDeploymentEnvironment =
  'production' | 'staging' | 'development' | 'local';

/**
 * Socket hosts a descriptor may steer this build to, per deployment
 * environment — the allowlist of ADR-078 Amendment 1, 6-F item 3.
 *
 * SMS talks to the AIM API host (`env-config.js` rewrites `sms.` to `aim.`),
 * so that is the socket host each environment ships. The configured socket
 * host is permitted on top of this list: it is the host that minted the
 * ticket, which is how a white-label API host (`safe-ms.` → `safe.`, ADR 115)
 * stays reachable without a new edge. What this list adds is the hosts a
 * descriptor may steer *away* to, so a compromised ticket response can only
 * choose among hosts already shipped here.
 */
export const SOCKET_HOST_ALLOWLIST: Readonly<
  Record<SocketDeploymentEnvironment, readonly string[]>
> = {
  production: ['aim.aegix.global'],
  staging: ['aim.stage.aegix.global'],
  development: ['aim.dev.aegix.global'],
  local: [
    'localhost',
    '127.0.0.1',
    'tiaki.localhost',
    'aim.tiaki.localhost',
    'sms.tiaki.localhost',
  ],
};

/**
 * The environment whose allowlist applies, from the runtime env-config keys
 * Sentry setup reads (the misspelled key is the one the generator emits). An
 * unrecognized value resolves to `local`, whose allowlist covers nothing
 * deployed, so only the configured host stays reachable.
 */
export function deploymentEnvironment(): SocketDeploymentEnvironment {
  const configured =
    window.__ENV?.NX_SENTRY_ENVIROMENT ||
    window.__ENV?.NX_SENTRY_ENVIRONMENT ||
    environment.sentryEnvironment;
  switch (configured) {
    case 'production':
      return 'production';
    case 'staging':
      return 'staging';
    case 'develop':
    case 'development':
      return 'development';
    default:
      return 'local';
  }
}

/**
 * The build's own socket endpoint: `NX_SOCKET_URL` when set, otherwise the
 * API host over `ws:`/`wss:`. Null when neither is configured, in which case
 * the socket stays idle.
 */
export function configuredSocketEndpoint(): string | null {
  const socketUrl = window.__ENV?.NX_SOCKET_URL;
  if (socketUrl) return `${socketUrl.replace(/\/+$/, '')}${WS_SOCKET_PATH}`;

  const apiUrl = window.__ENV?.NX_API_URL;
  if (!apiUrl) return null;

  const wsProtocol = apiUrl.startsWith('https') ? 'wss' : 'ws';
  const host = apiUrl.replace(/^https?:\/\//, '').replace(/\/+$/, '');
  return `${wsProtocol}://${host}${WS_SOCKET_PATH}`;
}

/** Hosts this build accepts a socket connection to. */
export function socketHostAllowlist(): string[] {
  const hosts = [...SOCKET_HOST_ALLOWLIST[deploymentEnvironment()]];
  const configuredHost = socketEndpointHost(configuredSocketEndpoint());
  if (configuredHost && !hosts.includes(configuredHost)) {
    hosts.push(configuredHost);
  }
  return hosts;
}

/**
 * Report a refused endpoint. Host and reason only: the ticket authorizes a
 * connect and Sentry is a third-party processor (ADR-098 / ADR-099 §3), so
 * neither it nor the full URL that could carry it ever leaves the client.
 */
export function reportRejectedSocketEndpoint(
  reason: SocketEndpointRejection,
  host: string | null,
): void {
  Sentry.captureException(
    new Error(`Socket descriptor endpoint rejected: ${reason}`),
    {
      tags: { surface: 'socket-descriptor', outcome: reason },
      contexts: {
        socketDescriptor: {
          host: host ?? null,
          environment: deploymentEnvironment(),
        },
      },
    },
  );
}

/**
 * The connect plan for a `ws/ticket` response, or null when it carries no
 * usable ticket or no endpoint to try. Advertised endpoints outside
 * `socketHostAllowlist()` are reported and skipped; with none left, the
 * configured endpoint is the single candidate.
 */
export function buildSocketConnectionPlan(
  payload: unknown,
): SocketConnectionPlan | null {
  return buildPlanFromDescriptor(payload, {
    allowlist: socketHostAllowlist(),
    configuredEndpoint: configuredSocketEndpoint(),
    onRejected: reportRejectedSocketEndpoint,
  });
}
